import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AppLanguages, buildMultiLanguageRoutesFragment } from './guards/translation';
import { OuterRouteResolver } from './resolvers/outer-route-resolver';
import { SubscriptionsStateInitGuard } from './guards/subscriptions-state-init';
import { AuthGuard } from './guards/auth';
import { CloudStateInitGuard } from './guards/cloud-state-init';

const routes: Routes = buildMultiLanguageRoutesFragment([
  // external
  {
    path: 'how-it-works',
    data: {
      external: {
        [AppLanguages.ru]:
          'https://closapp.notion.site/HOW-IT-WORKS-8a030c9c574e43bca346646dfd19ee83',
        [AppLanguages.en]:
          'https://closapp.notion.site/HOW-IT-WORKS-8a030c9c574e43bca346646dfd19ee83',
      },
    },
    resolve: { res: OuterRouteResolver },
    loadChildren: () => import('./routes/loading/loading.module').then((m) => m.LoadingModule),
  },
  {
    path: 'appstore',
    data: {
      external: {
        [AppLanguages.ru]: 'https://apps.apple.com/ru/app/clos-remote-shooting/id1509034647',
        [AppLanguages.en]: 'https://apps.apple.com/us/app/clos-remote-shooting/id1509034647',
      },
    },
    resolve: { res: OuterRouteResolver },
    loadChildren: () => import('./routes/loading/loading.module').then((m) => m.LoadingModule),
  },
  {
    path: 'playmarket',
    data: {
      external: {
        [AppLanguages.ru]:
          'https://play.google.com/store/apps/details?id=com.unoproduction.closapp&hl=ru&gl=RU',
        [AppLanguages.en]:
          'https://play.google.com/store/apps/details?id=com.unoproduction.closapp&hl=en&gl=US',
      },
    },
    resolve: { use: OuterRouteResolver },
    loadChildren: () => import('./routes/loading/loading.module').then((m) => m.LoadingModule),
  },
  {
    path: 'privacypolicy',
    data: {
      external: 'https://clos.vc/p/privacy-policy',
    },
    loadChildren: () => import('./routes/loading/loading.module').then((m) => m.LoadingModule),
    resolve: { res: OuterRouteResolver },
  },
  {
    path: 'promo',
    data: { external: 'https://clos.vc/' },
    resolve: { res: OuterRouteResolver },
    loadChildren: () => import('./routes/loading/loading.module').then((m) => m.LoadingModule),
  },
  {
    path: 'instagram',
    data: { external: 'https://www.instagram.com/clos.app/' },
    resolve: { res: OuterRouteResolver },
    loadChildren: () => import('./routes/loading/loading.module').then((m) => m.LoadingModule),
  },
  {
    path: 'terms-and-conditions',
    data: {
      external: 'https://clos.vc/p/end-user-license-agreement',
    },
    resolve: { res: OuterRouteResolver },
    loadChildren: () => import('./routes/loading/loading.module').then((m) => m.LoadingModule),
  },
  {
    // Public, unauthenticated pricing page. Reflects the live RevenueCat
    // offering so it always matches checkout (required for Paddle domain review).
    path: 'pricing',
    loadChildren: () => import('./routes/pricing/pricing.module').then((m) => m.PricingModule),
  },

  // internal
  {
    path: 'join',
    loadComponent: () =>
      import('./routes/invite-join/invite-join.component').then((m) => m.InviteJoinComponent),
  },
  {
    path: '',
    canActivate: [AuthGuard, CloudStateInitGuard, SubscriptionsStateInitGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import(/* webpackChunkName: "main-route" */ './routes/main').then((m) => m.MainModule),
      },
      {
        path: 'sessions',
        loadChildren: () =>
          import(/* webpackChunkName: "rooms-route" */ './routes/rooms/rooms.module').then(
            (m) => m.RoomsModule,
          ),
      },
      {
        path: 'rooms',
        loadChildren: () =>
          import(/* webpackChunkName: "rooms-route" */ './routes/rooms/rooms.module').then(
            (m) => m.RoomsModule,
          ),
      },
      {
        path: 'upload',
        loadChildren: () =>
          import(/* webpackChunkName: "upload-route" */ './routes/archive-inspections').then(
            (m) => m.ArchiveInspectionsModule,
          ),
      },
      {
        path: 'archives',
        loadChildren: () =>
          import(/* webpackChunkName: "archives-route" */ './routes/archive-inspections').then(
            (m) => m.ArchiveInspectionsModule,
          ),
      },
      {
        path: '_legacy/gallery',
        loadChildren: () => import('./routes/gallery/gallery.module').then((m) => m.GalleryModule),
      },
      {
        path: '_legacy/welcome',
        loadChildren: () => import('./routes/welcome/welcome.module').then((m) => m.WelcomeModule),
      },
    ],
  },
  {
    path: 'login',
    loadChildren: () =>
      import(/* webpackChunkName: "auth-route" */ './routes/auth/auth.module').then(
        (m) => m.AuthModule,
      ),
  },
  {
    path: '404',
    loadChildren: () =>
      import(/* webpackChunkName: "404-route" */ './routes/not-found/not-found.module').then(
        (m) => m.NotFoundModule,
      ),
  },
  {
    path: 'loading',
    loadChildren: () => import('./routes/loading/loading.module').then((m) => m.LoadingModule),
  },
  {
    path: 'welcome',
    redirectTo: '',
  },
  {
    path: 'gallery/:roomId',
    redirectTo: 'archives/:roomId',
  },
  {
    path: 'gallery',
    redirectTo: '',
  },
  { path: '**', redirectTo: '404' },
]);

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      initialNavigation: 'enabledBlocking',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
